import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Separator,
  Spinner,
  Textarea,
} from '@repo/shared-client/components/ui'
import { useTranslation } from '@web/src/lib/intl/react'
import dayjs from 'dayjs'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type {
  AdminAdjustProfilePointsMutation,
  AdminAdjustProfilePointsMutationVariables,
  AdminPointsEditQuery,
  AdminPointsEditQueryVariables,
} from 'types/graphql'

export const ADMIN_POINTS_EDIT_QUERY: TypedDocumentNode<AdminPointsEditQuery, AdminPointsEditQueryVariables> = gql`
  query AdminPointsEditQuery($profileId: String!, $page: Int) {
    adminProfilePointBalance(profileId: $profileId) {
      id
      availablePoints
      lifetimeEarnedPoints
      redeemedPoints
      adjustedPoints
      reversedPoints
      supporterMonthThreshold
      canRedeemSupporterMonth
    }
    adminProfilePointTransactionsPage(profileId: $profileId, page: $page) {
      count
      perPage
      entries {
        id
        createdAt
        points
        type
        status
        reason
        awardKey
        adminNote
        reversesPointTransactionId
        reversedByPointTransactionId
      }
    }
  }
`

const ADMIN_ADJUST_PROFILE_POINTS: TypedDocumentNode<
  AdminAdjustProfilePointsMutation,
  AdminAdjustProfilePointsMutationVariables
> = gql`
  mutation AdminAdjustProfilePoints($input: AdminAdjustProfilePointsInput!) {
    adminAdjustProfilePoints(input: $input) {
      id
      points
      reason
      adminNote
    }
  }
`

type PointsEditUser = {
  profile?: {
    id: string
    deletedAt?: string | null
  } | null
}

type PointsEditProps = {
  user: PointsEditUser
  onPointsUpdated?: () => void | Promise<void>
}

export default function PointsEdit({ onPointsUpdated, user }: PointsEditProps) {
  const { t } = useTranslation()
  const client = useApolloClient()
  const profileId = user.profile?.id
  const [page, setPage] = useState(1)
  const [points, setPoints] = useState('')
  const [adminNote, setAdminNote] = useState('')

  const { data, networkStatus } = useQuery(ADMIN_POINTS_EDIT_QUERY, {
    skip: !profileId,
    variables: { page, profileId: profileId ?? '' },
  })

  const [adjustPoints, { loading: adjusting }] = useMutation(ADMIN_ADJUST_PROFILE_POINTS, {
    onCompleted: async () => {
      toast.success(t('Point adjustment saved'))
      setPoints('')
      setAdminNote('')
      await client.refetchQueries({ include: [ADMIN_POINTS_EDIT_QUERY] })
      await onPointsUpdated?.()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  if (!profileId) {
    return (
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle className="text-base">{t('No profile')}</CardTitle>
          <CardDescription>{t('This user record has no linked profile yet.')}</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const balance = data?.adminProfilePointBalance
  const transactionPage = data?.adminProfilePointTransactionsPage
  const totalPages = transactionPage ? Math.max(1, Math.ceil(transactionPage.count / transactionPage.perPage)) : 1
  const adjustmentValue = Number.parseInt(points, 10)
  const canSubmit = Number.isFinite(adjustmentValue) && adjustmentValue !== 0 && adminNote.trim().length > 0

  const submitAdjustment = async () => {
    if (!canSubmit) return
    await adjustPoints({
      variables: {
        input: {
          adminNote,
          points: adjustmentValue,
          profileId,
        },
      },
    })
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 md:grid-cols-4">
        <Card>
          <CardHeader>
            <CardDescription>{t('Available')}</CardDescription>
            <CardTitle>{balance?.availablePoints ?? '—'}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader>
            <CardDescription>{t('Lifetime earned')}</CardDescription>
            <CardTitle>{balance?.lifetimeEarnedPoints ?? '—'}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader>
            <CardDescription>{t('Redeemed')}</CardDescription>
            <CardTitle>{balance?.redeemedPoints ?? '—'}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader>
            <CardDescription>{t('Reversed')}</CardDescription>
            <CardTitle>{balance?.reversedPoints ?? '—'}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('Adjust points')}</CardTitle>
          <CardDescription>{t('Use positive or negative values. An admin note is required.')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid gap-4 md:grid-cols-[180px_minmax(0,1fr)]">
            <div className="flex flex-col gap-2">
              <Label htmlFor="points-adjustment">{t('Points')}</Label>
              <Input
                id="points-adjustment"
                onChange={(event) => setPoints(event.target.value)}
                placeholder={t('Example: 25 or -10')}
                type="number"
                value={points}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="points-admin-note">{t('Admin note')}</Label>
              <Textarea
                id="points-admin-note"
                onChange={(event) => setAdminNote(event.target.value)}
                placeholder={t('Explain why this adjustment is needed')}
                value={adminNote}
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button disabled={!canSubmit || adjusting || Boolean(user.profile?.deletedAt)} onClick={submitAdjustment}>
              {adjusting ? <Spinner className="size-4" /> : null}
              {t('Save adjustment')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('Point history')}</CardTitle>
          <CardDescription>
            {t('Awards, reversals, redemptions, and manual adjustments for this profile.')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {networkStatus === NetworkStatus.loading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : transactionPage?.entries.length ? (
            <div className="flex flex-col gap-3">
              {transactionPage.entries.map((transaction) => (
                <div className="flex flex-col gap-2 rounded-lg border border-border p-3" key={transaction.id}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="font-medium">{transaction.reason}</div>
                      <div className="text-muted-foreground text-sm">
                        {dayjs(transaction.createdAt).format('MMM D, YYYY h:mm A')}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{transaction.type}</Badge>
                      <span className={transaction.points >= 0 ? 'font-semibold text-green-600' : 'font-semibold'}>
                        {transaction.points >= 0 ? '+' : ''}
                        {transaction.points}
                      </span>
                    </div>
                  </div>
                  {transaction.adminNote ? (
                    <p className="text-muted-foreground text-sm">{transaction.adminNote}</p>
                  ) : null}
                  {transaction.reversesPointTransactionId || transaction.reversedByPointTransactionId ? (
                    <p className="text-muted-foreground text-xs">
                      {transaction.reversesPointTransactionId
                        ? t('Reverses transaction {{id}}', { id: transaction.reversesPointTransactionId })
                        : t('Reversed by transaction {{id}}', { id: transaction.reversedByPointTransactionId })}
                    </p>
                  ) : null}
                </div>
              ))}
              <Separator />
              <div className="flex items-center justify-between gap-2">
                <Button
                  disabled={page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  variant="outline"
                >
                  {t('Previous')}
                </Button>
                <span className="text-muted-foreground text-sm">
                  {t('Page {{page}} of {{totalPages}}', { page, totalPages })}
                </span>
                <Button
                  disabled={page >= totalPages}
                  onClick={() => setPage((current) => current + 1)}
                  variant="outline"
                >
                  {t('Next')}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">{t('No point transactions yet.')}</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
